/**
 * SideCart — Hallmark cart drawer
 *
 * Architecture:
 *  - Single class owns all drawer state and DOM interactions.
 *  - Content is refreshed via Shopify Section Rendering API (?sections=side-cart).
 *  - Cart mutations go to /cart/change.js and /cart/add.js.
 *  - Listens for theme:cartchanged (existing theme event) to refresh drawer.
 *  - Dispatches theme:cartchanged itself after mutations to sync header count + cart page.
 *  - Free gift picker is an in-drawer view; uses data embedded in the section HTML.
 *  - AbortController cancels in-flight fetches on rapid actions.
 *  - Quantity changes are debounced to 300ms.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  Constants                                                           */
  /* ------------------------------------------------------------------ */
  const SECTION_ID      = 'side-cart';
  const OPEN_EVENT      = 'theme:sidecart:open';
  const CART_CHANGED    = 'theme:cartchanged';
  const DEBOUNCE_MS     = 300;
  const TRANSITION_MS   = 350;

  /* ------------------------------------------------------------------ */
  /*  SideCart class                                                      */
  /* ------------------------------------------------------------------ */
  class SideCart {
    constructor() {
      this._drawer     = null;
      this._overlay    = null;
      this._announce   = null;
      this._loadingEl  = null;
      this._opener     = null; // element that opened the drawer (for focus return)
      this._isOpen     = false;
      this._isBusy     = false;
      this._abortCtrl  = null;
      this._debounceTimers = new Map(); // key → timer id (per line item)

      // Wait for DOM
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._init());
      } else {
        this._init();
      }
    }

    /* ---------------------------------------------------------------- */
    /*  Initialisation                                                    */
    /* ---------------------------------------------------------------- */
    _init() {
      this._drawer   = document.getElementById('side-cart');
      this._overlay  = document.getElementById('side-cart-overlay');
      this._announce = document.getElementById('side-cart-announce');

      if (!this._drawer) return; // section not present (e.g. theme editor preview)

      this._loadingEl = this._drawer.querySelector('.side-cart__loading');

      // Read config from data attributes (set by Liquid)
      this._cookieName        = this._drawer.dataset.cookieName        || 'free_product_gift_added';
      this._giftEnabled       = this._drawer.dataset.giftEnabled       === 'true';
      this._giftThreshold     = parseFloat(this._drawer.dataset.giftThreshold) || 0;
      this._giftCollection    = this._drawer.dataset.giftCollection    || '';
      this._storefrontToken   = this._drawer.dataset.storefrontToken   || '';

      this._bindStaticEvents();
      this._bindDelegatedEvents();
      this._bindThemeEvents();
      this._interceptQuickAdd();

      // On page load: check cookie resilience (mirrors free-product-picker's listenForCartChanges)
      if (this._giftEnabled) this._syncGiftCookieOnLoad();
    }

    /* ---------------------------------------------------------------- */
    /*  Event binding — static elements (always present)                 */
    /* ---------------------------------------------------------------- */
    _bindStaticEvents() {
      // Overlay click → close
      if (this._overlay) {
        this._overlay.addEventListener('click', () => this.close());
      }

      // Close button inside drawer
      this._drawer.addEventListener('click', (e) => {
        if (e.target.closest('[data-side-cart-close]')) {
          e.preventDefault();
          this.close();
        }
      });

      // ESC to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this._isOpen) this.close();
      });

      // Header / any element with data-side-cart-open
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-side-cart-open]');
        if (!trigger) return;
        e.preventDefault();
        this._opener = trigger;
        this.open();
      });

      // Continue shopping → close
      this._drawer.addEventListener('click', (e) => {
        if (e.target.closest('[data-side-cart-continue]')) {
          e.preventDefault();
          this.close();
        }
      });
    }

    /* ---------------------------------------------------------------- */
    /*  Event binding — delegated (works after section re-render)         */
    /* ---------------------------------------------------------------- */
    _bindDelegatedEvents() {
      // Quantity decrease
      this._drawer.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-side-cart-qty-down]');
        if (!btn) return;
        e.preventDefault();
        this._adjustQty(btn, -1);
      });

      // Quantity increase
      this._drawer.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-side-cart-qty-up]');
        if (!btn) return;
        e.preventDefault();
        this._adjustQty(btn, +1);
      });

      // Quantity input manual change
      this._drawer.addEventListener('change', (e) => {
        const input = e.target.closest('[data-side-cart-qty-input]');
        if (!input) return;
        const line = parseInt(input.dataset.line, 10);
        const qty  = Math.max(0, parseInt(input.value, 10) || 0);
        this._debouncedUpdate(line, qty, input);
      });

      // Remove
      this._drawer.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-side-cart-remove]');
        if (!btn) return;
        e.preventDefault();
        this._removeItem(btn);
      });

      // Free gift banner CTA → show gift picker
      this._drawer.addEventListener('click', (e) => {
        if (e.target.closest('[data-side-cart-gift-picker]')) {
          e.preventDefault();
          this._showGiftPicker();
        }
      });

      // Gift picker "Add to basket"
      this._drawer.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-side-cart-gift-add]');
        if (!btn) return;
        e.preventDefault();
        this._addFreeGift(btn);
      });

      // Gift picker "Continue" → return to cart
      this._drawer.addEventListener('click', (e) => {
        if (e.target.closest('[data-side-cart-gift-continue]')) {
          e.preventDefault();
          this._hideGiftPicker();
        }
      });

      // POD radio change → update cart properties + clear error state
      this._drawer.addEventListener('change', (e) => {
        const radio = e.target.closest('[data-pod-radio]');
        if (!radio) return;
        this._updatePodProperty(radio);
        this._clearPodSendErrorFor(radio);
      });

      // Checkout button click → validate POD send-to selections first
      this._drawer.addEventListener('click', (e) => {
        const checkoutBtn = e.target.closest('#sc-checkout-btn, .side-cart__checkout-btn');
        if (!checkoutBtn) return;
        const result = this._validatePodSendOptions();
        if (!result.valid) {
          e.preventDefault();
          this._showPodSendErrors(result.invalidGroups);
        }
      });

      // Discount code apply / remove
      this._drawer.addEventListener('click', (e) => {
        if (e.target.closest('[data-side-cart-discount-apply]')) {
          e.preventDefault();
          this._applyDiscount();
        }
        if (e.target.closest('[data-side-cart-discount-remove]')) {
          e.preventDefault();
          this._removeDiscountCodes();
        }
      });

      // Toggle Apply button active state based on input content
      this._drawer.addEventListener('input', (e) => {
        const input = e.target.closest('[data-side-cart-discount-input]');
        if (!input) return;
        const btn = this._drawer.querySelector('[data-side-cart-discount-apply]');
        if (btn) btn.classList.toggle('is-active', input.value.trim().length > 0);
      });
    }

    /* ---------------------------------------------------------------- */
    /*  Theme event integration                                           */
    /* ---------------------------------------------------------------- */
    _bindThemeEvents() {
      // Refresh drawer content whenever cart changes (add-to-cart, cart page updates)
      document.documentElement.addEventListener(CART_CHANGED, () => {
        this._refreshContent();
      });

      // Custom event to open drawer (fired by theme.js after add-to-cart)
      document.documentElement.addEventListener(OPEN_EVENT, () => {
        this.open();
      });
    }

    /* ---------------------------------------------------------------- */
    /*  Intercept quick-add forms (collection grids, featured products)    */
    /* ---------------------------------------------------------------- */
    _interceptQuickAdd() {
      // Capture phase: runs before jQuery's bubbling handler in theme.js,
      // so all add-to-cart forms open the side cart regardless of cart_type.
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action="/cart/add"], form[action*="/cart/add"]');
        if (!form) return;

        // For product-purchase-form: run theme's variant validation first
        if (form.classList.contains('product-purchase-form')) {
          const $container = $(form).closest('.product-form');
          if ($container.length && typeof theme.validateProductForm === 'function') {
            if (!theme.validateProductForm($container)) return; // let the native error show
          }
        }

        e.preventDefault();
        e.stopPropagation();

        const btn = form.querySelector('button[type="submit"], input[type="submit"]');
        if (btn) {
          btn.disabled = true;
          btn.classList.add('is-adding');
        }

        const formData = new FormData(form);

        fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
          .then((r) => {
            if (!r.ok) return r.json().then((d) => Promise.reject(d));
            return r.json();
          })
          .then(() => {
            this._dispatchCartChanged();
            this.open();
            if (btn) {
              btn.disabled = false;
              btn.classList.remove('is-adding');
            }
          })
          .catch((err) => {
            console.warn('[SideCart] quick-add failed:', err);
            if (btn) {
              btn.disabled = false;
              btn.classList.remove('is-adding');
            }
            if (err && err.description) {
              alert(err.description);
            }
          });
      }, true);
    }

    /* ---------------------------------------------------------------- */
    /*  Open / Close                                                      */
    /* ---------------------------------------------------------------- */
    open() {
      if (this._isOpen) return;
      this._isOpen = true;

      // Refresh content first to ensure cart is current
      this._refreshContent({ silent: true });

      this._drawer.classList.add('is-open');
      this._drawer.setAttribute('aria-hidden', 'false');
      if (this._overlay) this._overlay.classList.add('is-visible');
      document.body.classList.add('side-cart-open');

      // Focus first focusable element
      requestAnimationFrame(() => {
        const firstFocusable = this._drawer.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (firstFocusable) firstFocusable.focus();
      });

      this._trapFocus();
    }

    close() {
      if (!this._isOpen) return;
      this._isOpen = false;

      this._drawer.classList.remove('is-open');
      this._drawer.setAttribute('aria-hidden', 'true');
      if (this._overlay) this._overlay.classList.remove('is-visible');
      document.body.classList.remove('side-cart-open');

      this._releaseFocusTrap();

      // Return focus to trigger element
      if (this._opener && document.contains(this._opener)) {
        this._opener.focus();
      }

      // Hide gift picker if open
      this._hideGiftPicker();
    }

    /* ---------------------------------------------------------------- */
    /*  Focus trap                                                        */
    /* ---------------------------------------------------------------- */
    _trapFocus() {
      this._focusTrapHandler = (e) => {
        if (!this._isOpen) return;
        const focusables = Array.from(
          this._drawer.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.closest('[aria-hidden="true"]'));

        if (focusables.length === 0) return;
        const first = focusables[0];
        const last  = focusables[focusables.length - 1];

        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      document.addEventListener('keydown', this._focusTrapHandler);
    }

    _releaseFocusTrap() {
      if (this._focusTrapHandler) {
        document.removeEventListener('keydown', this._focusTrapHandler);
        this._focusTrapHandler = null;
      }
    }

    /* ---------------------------------------------------------------- */
    /*  Content refresh via Section Rendering API                         */
    /* ---------------------------------------------------------------- */
    _refreshContent(options = {}) {
      // Abort any in-flight refresh
      if (this._abortCtrl) this._abortCtrl.abort();
      this._abortCtrl = new AbortController();

      if (!options.silent) this._setLoading(true);

      return fetch(`/?sections=${SECTION_ID}`, {
        signal: this._abortCtrl.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((r) => r.json())
        .then((data) => {
          if (data[SECTION_ID]) {
            this._replaceContent(data[SECTION_ID]);
          }
          this._setLoading(false);
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            console.warn('[SideCart] refresh failed:', err);
            this._setLoading(false);
          }
        });
    }

    /**
     * Replace inner content with the freshly-rendered section HTML.
     * Preserves the drawer shell and avoids re-binding static events.
     */
    _replaceContent(sectionHtml) {
      const parser = new DOMParser();
      const doc    = parser.parseFromString(sectionHtml, 'text/html');
      const newInner = doc.getElementById('side-cart');
      if (!newInner) return;

      // Strip is-qualified from the incoming HTML before injecting —
      // JS is the sole source of truth for banner state, so we never let
      // a stale Liquid render flash the banner on then off.
      const incomingBanner = newInner.querySelector('#sc-gift-banner');
      if (incomingBanner) incomingBanner.classList.remove('is-qualified');

      // Swap inner regions only (body + footer) to avoid re-triggering CSS transitions
      const regions = ['side-cart__body', 'side-cart__footer', 'side-cart__gift-picker'];
      regions.forEach((cls) => {
        const existing = this._drawer.querySelector(`.${cls}`);
        const fresh    = newInner.querySelector(`.${cls}`);
        if (existing && fresh) {
          existing.innerHTML = fresh.innerHTML;
        }
      });

      // Update delivery bar fill width
      this._updateDeliveryBar(newInner);

      // Update empty/filled state class
      const isEmpty = newInner.classList.contains('is-empty');
      this._drawer.classList.toggle('is-empty', isEmpty);

      // Announce to screen readers
      const count = this._drawer.querySelector('[data-sc-item-count]');
      if (count && this._announce) {
        this._announce.textContent = count.dataset.scItemCount || '';
      }

      // Re-evaluate gift qualification client-side after every content swap
      if (this._giftEnabled) this._checkGiftQualification();
    }

    _updateDeliveryBar(source) {
      const srcBar = source.querySelector('.side-cart__delivery-bar-wrap');
      const dstBar = this._drawer.querySelector('.side-cart__delivery-bar-wrap');
      if (!srcBar || !dstBar) return;
      dstBar.innerHTML = srcBar.innerHTML;
      dstBar.className = srcBar.className;
    }

    /* ---------------------------------------------------------------- */
    /*  Free gift qualification — client-side check                      */
    /*  Mirrors free-product-picker's calculateCartTotal + showModal     */
    /* ---------------------------------------------------------------- */

    /**
     * Fetch /cart.js, optionally filter by collection, then toggle
     * the is-qualified class on the banner. This is the single source of
     * truth for qualification state after every content refresh.
     */
    async _checkGiftQualification() {
      const banner = this._drawer.querySelector('#sc-gift-banner');
      if (!banner) return;

      try {
        const r    = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const cart = await r.json();

        // Check if a free gift is already in the cart (any item with _free_gift property)
        const giftAlreadyInCart = cart.items.some(
          (item) => item.properties && item.properties['_free_gift']
        );

        if (giftAlreadyInCart) {
          banner.classList.remove('is-qualified');
          if (!this._getCookie(this._cookieName)) {
            this._setCookie(this._cookieName, 'true', 30);
          }
          return;
        }

        // Gift NOT in cart — clear cookie so both side-cart banner and
        // free-product-picker modal can re-evaluate qualification.
        if (this._getCookie(this._cookieName)) {
          this._eraseCookie(this._cookieName);
        }

        // Calculate qualifying cart total (optionally filtered by collection)
        const cartTotal = await this._calculateGiftTotal(cart);
        const thresholdMet = cartTotal >= this._giftThreshold;

        banner.classList.toggle('is-qualified', thresholdMet);
      } catch (err) {
        console.warn('[SideCart] gift qualification check failed:', err);
      }
    }

    /**
     * Sum cart line prices, optionally filtering to items in the
     * configured collection (mirrors free-product-picker's calculateCartTotal).
     */
    async _calculateGiftTotal(cart) {
      if (!this._giftCollection) {
        // No collection filter — use all items (convert cents → pounds)
        return cart.items.reduce((sum, item) => sum + item.line_price, 0) / 100;
      }

      let total = 0;
      for (const item of cart.items) {
        try {
          const inCollection = await this._isProductInCollection(item.product_id, this._giftCollection);
          if (inCollection) total += item.line_price;
        } catch {
          // On error include the item (matches free-product-picker fallback)
          total += item.line_price;
        }
      }
      return total / 100;
    }

    /**
     * Check if a product belongs to a collection via Storefront GraphQL API.
     * Identical approach to free-product-picker's isProductInCollection.
     */
    async _isProductInCollection(productId, collectionHandle) {
      if (!this._storefrontToken) return true; // no token → can't filter, include all

      const query = `
        query getProduct($id: ID!) {
          product(id: $id) {
            collections(first: 250) {
              edges { node { handle } }
            }
          }
        }
      `;

      try {
        const r = await fetch('/api/2025-04/graphql.json', {
          method:  'POST',
          headers: {
            'Content-Type':                     'application/json',
            'X-Shopify-Storefront-Access-Token': this._storefrontToken,
          },
          body: JSON.stringify({ query, variables: { id: `gid://shopify/Product/${productId}` } }),
        });

        if (!r.ok) throw new Error('Storefront API error');

        const data        = await r.json();
        const collections = data.data?.product?.collections?.edges || [];
        return collections.some((edge) => edge.node.handle === collectionHandle);
      } catch (err) {
        console.warn('[SideCart] isProductInCollection failed:', err);
        return true; // fail open — include item in total
      }
    }

    /**
     * On page load: if the cookie exists but no free gift is in the cart,
     * erase the stale cookie so qualification can be re-evaluated.
     * Mirrors free-product-picker's listenForCartChanges.
     */
    async _syncGiftCookieOnLoad() {
      try {
        const r    = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const cart = await r.json();

        const hasFreeGift = cart.items.some(
          (item) => item.properties && item.properties['_free_gift']
        );
        const hasCookie = !!this._getCookie(this._cookieName);

        if (hasFreeGift && !hasCookie) {
          this._setCookie(this._cookieName, 'true', 30);
        } else if (!hasFreeGift && hasCookie) {
          this._eraseCookie(this._cookieName);
        }
      } catch (err) {
        console.warn('[SideCart] syncGiftCookieOnLoad failed:', err);
      }
    }

    _eraseCookie(name) {
      document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    }

    /* ---------------------------------------------------------------- */
    /*  Loading state                                                     */
    /* ---------------------------------------------------------------- */
    _setLoading(on) {
      if (!this._loadingEl) return;
      this._loadingEl.classList.toggle('is-visible', on);
    }

    /* ---------------------------------------------------------------- */
    /*  Cart mutations                                                    */
    /* ---------------------------------------------------------------- */

    /** Adjust quantity via +/- buttons */
    _adjustQty(btn, delta) {
      const item  = btn.closest('[data-side-cart-item]');
      if (!item) return;
      const input = item.querySelector('[data-side-cart-qty-input]');
      if (!input) return;
      const line   = parseInt(input.dataset.line, 10);
      const newQty = Math.max(0, (parseInt(input.value, 10) || 1) + delta);

      if (newQty === 0) {
        item.classList.add('is-removing');
        this._debouncedUpdate(line, 0, input);
        return;
      }

      input.value = newQty;
      input.classList.add('pulse');
      input.addEventListener('animationend', () => input.classList.remove('pulse'), { once: true });

      this._debouncedUpdate(line, newQty, input);
    }

    /** Debounced cart/change call — aborts previous timer for same line */
    _debouncedUpdate(line, qty, inputEl) {
      const key = `line-${line}`;
      if (this._debounceTimers.has(key)) {
        clearTimeout(this._debounceTimers.get(key));
      }
      const id = setTimeout(() => {
        this._debounceTimers.delete(key);
        this._updateCart({ line, quantity: qty });
      }, DEBOUNCE_MS);
      this._debounceTimers.set(key, id);
    }

    /** POST /cart/change.js */
    _updateCart(params) {
      if (this._isBusy) return;
      this._isBusy = true;
      this._setLoading(true);
      this._hideError();

      return fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify(params),
      })
        .then((r) => {
          if (!r.ok) return r.json().then((d) => Promise.reject(d));
          return r.json();
        })
        .then(() => {
          this._dispatchCartChanged();
          return this._refreshContent();
        })
        .catch((err) => {
          console.warn('[SideCart] update failed:', err);
          this._showError(err?.description || 'Could not update cart. Please try again.');
          this._setLoading(false);
        })
        .finally(() => {
          this._isBusy = false;
        });
    }

    /** Remove item with exit animation */
    _removeItem(btn) {
      const item = btn.closest('[data-side-cart-item]');
      if (!item) return;
      const key = item.dataset.itemKey;

      item.classList.add('is-removing');
      item.addEventListener(
        'animationend',
        () => {
          this._updateCart({ id: key, quantity: 0 });
        },
        { once: true }
      );
    }

    /** POST /cart/add.js for free gift */
    _addFreeGift(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.disabled = true;
      this._setLoading(true);
      this._hideError();

      // Set cookie so free-product-picker section doesn't re-trigger modal
      this._setCookie('free_product_gift_added', 'true', 30);

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1, properties: { _free_gift: 'true' } }],
        }),
      })
        .then((r) => r.json())
        .then(() => {
          this._dispatchCartChanged();
          this._hideGiftPicker();
          return this._refreshContent();
        })
        .catch((err) => {
          console.warn('[SideCart] add free gift failed:', err);
          this._showError('Could not add free gift. Please try again.');
          btn.disabled = false;
          this._setLoading(false);
        });
    }

    /** Update POD card send-to property */
    _updatePodProperty(radio) {
      const item = radio.closest('[data-side-cart-item]');
      if (!item) return;
      const line = parseInt(item.dataset.line, 10);
      const key  = radio.name.replace(/\[.*$/, ''); // extract base property key
      const val  = radio.value;

      // Fetch current properties then merge update
      fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((r) => r.json())
        .then((cart) => {
          const cartItem = cart.items[line - 1];
          if (!cartItem) return;
          const props = Object.assign({}, cartItem.properties);
          // The property key in Liquid is _send_to_myself_<item.key>
          Object.keys(props).forEach((k) => {
            if (k.startsWith('_send_to_myself')) props[k] = val;
          });
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'Personalisation_complete',
            delivery_option: val === 'false' ? 'Direct_to_recipient' : 'Standard_delivery',
            Product_id: cartItem.product_id ? String(cartItem.product_id) : '',
            Project_id: (cartItem.properties && cartItem.properties._project_id) || ''
          });
          return fetch('/cart/change.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body:    JSON.stringify({ line, properties: props }),
          });
        })
        .catch((err) => console.warn('[SideCart] pod property update failed:', err));
    }

    /**
     * Validate that every POD line in the cart has a send-to selection.
     * Returns { valid, invalidGroups } where invalidGroups is an array of
     * `[data-pod-send-group]` elements missing a checked radio.
     */
    _validatePodSendOptions() {
      const groups = this._drawer.querySelectorAll('[data-pod-send-group]');
      const invalidGroups = [];
      groups.forEach((group) => {
        const checked = group.querySelector('[data-pod-radio]:checked');
        if (!checked) invalidGroups.push(group);
      });
      return { valid: invalidGroups.length === 0, invalidGroups };
    }

    /**
     * Apply error state to invalid POD send-to groups, surface a global error
     * message, scroll the first invalid group into view, and announce via aria-live.
     */
    _showPodSendErrors(invalidGroups) {
      if (!invalidGroups || invalidGroups.length === 0) return;

      invalidGroups.forEach((group) => {
        group.classList.add('is-error');
        const item = group.closest('[data-side-cart-item]');
        if (item) {
          const inlineErr = item.querySelector('[data-pod-send-error]');
          if (inlineErr) inlineErr.classList.add('is-visible');
        }
      });

      const globalMsg = this._drawer.dataset.podSendGlobalMsg
        || 'Please choose a delivery option for every personalised card before checkout';
      this._showError(globalMsg);
      const errEl = this._drawer.querySelector('.side-cart__error-msg');
      if (errEl) errEl.classList.add('is-pod-error');
      if (this._announce) this._announce.textContent = globalMsg;

      const first = invalidGroups[0];
      first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const firstRadio = first.querySelector('[data-pod-radio]');
      if (firstRadio) {
        try { firstRadio.focus({ preventScroll: true }); } catch (_) { firstRadio.focus(); }
      }
    }

    /**
     * Clear POD send-to error state for the group containing `radio`,
     * and clear the global error if no other groups remain invalid.
     */
    _clearPodSendErrorFor(radio) {
      const group = radio.closest('[data-pod-send-group]');
      if (group) {
        group.classList.remove('is-error');
        const item = group.closest('[data-side-cart-item]');
        if (item) {
          const inlineErr = item.querySelector('[data-pod-send-error]');
          if (inlineErr) inlineErr.classList.remove('is-visible');
        }
      }
      if (this._validatePodSendOptions().valid) {
        const errEl = this._drawer.querySelector('.side-cart__error-msg');
        if (errEl && errEl.classList.contains('is-pod-error')) {
          this._hideError();
          errEl.classList.remove('is-pod-error');
          if (this._announce) this._announce.textContent = '';
        }
      }
    }

    /** Apply discount code via /cart/update.js (Shopify AJAX API — no redirect needed) */
    _applyDiscount() {
      const input = this._drawer.querySelector('[data-side-cart-discount-input]');
      if (!input) return;
      const code = (input.value || '').trim();
      if (!code) return;

      const applyBtn = this._drawer.querySelector('[data-side-cart-discount-apply]');
      if (applyBtn) applyBtn.disabled = true;
      this._hideDiscountError();
      this._setLoading(true);

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ discount: code }),
      })
        .then((r) => {
          if (!r.ok) return r.json().then((d) => Promise.reject(d));
          return r.json();
        })
        .then((cart) => {
          if (applyBtn) applyBtn.disabled = false;

          // Check both cart-level AND line-level discounts.
          // Product discounts (e.g. "100% off products") appear only in
          // line_level_discount_allocations, not cart_level_discount_applications.
          const hasCartLevelDiscount =
            cart.cart_level_discount_applications &&
            cart.cart_level_discount_applications.length > 0;

          const hasLineLevelDiscount =
            cart.items &&
            cart.items.some(
              (item) =>
                item.line_level_discount_allocations &&
                item.line_level_discount_allocations.length > 0
            );

          const hasDiscount = hasCartLevelDiscount || hasLineLevelDiscount;

          if (!hasDiscount) {
            this._showDiscountError('Discount code is not valid, some discounts only apply in checkout');
            this._setLoading(false);
            return;
          }

          input.value = '';
          if (applyBtn) applyBtn.classList.remove('is-active');
          this._dispatchCartChanged();
          return this._refreshContent();
        })
        .catch((err) => {
          console.warn('[SideCart] discount apply failed:', err);
          this._showDiscountError(err?.description || err?.message || 'Could not apply discount. Please try again.');
          if (applyBtn) applyBtn.disabled = false;
          this._setLoading(false);
        });
    }

    /**
     * Clear all cart discount codes via AJAX (empty string).
     * Automatic / checkout-only discounts cannot be removed here — Shopify
     * does not expose them as codes on the cart.
     */
    _removeDiscountCodes() {
      this._hideDiscountError();
      this._setLoading(true);

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ discount: '' }),
      })
        .then((r) => {
          if (!r.ok) return r.json().then((d) => Promise.reject(d));
          return r.json();
        })
        .then(() => {
          this._dispatchCartChanged();
          return this._refreshContent();
        })
        .catch((err) => {
          console.warn('[SideCart] remove discount failed:', err);
          this._showDiscountError(
            err?.description || err?.message || 'Could not remove discount. Please try again.'
          );
          this._setLoading(false);
        });
    }

    /* ---------------------------------------------------------------- */
    /*  Gift picker view                                                  */
    /* ---------------------------------------------------------------- */
    _showGiftPicker() {
      this._drawer.classList.add('is-gift-picker-mode');
      const picker = this._drawer.querySelector('.side-cart__gift-picker');
      if (picker) {
        picker.setAttribute('aria-hidden', 'false');
        const firstBtn = picker.querySelector('button, a, [tabindex]');
        if (firstBtn) firstBtn.focus();
      }
    }

    _hideGiftPicker() {
      this._drawer.classList.remove('is-gift-picker-mode');
      const picker = this._drawer.querySelector('.side-cart__gift-picker');
      if (picker) picker.setAttribute('aria-hidden', 'true');
    }

    /* ---------------------------------------------------------------- */
    /*  Error messaging                                                   */
    /* ---------------------------------------------------------------- */
    _showError(msg) {
      let el = this._drawer.querySelector('.side-cart__error-msg');
      if (!el) {
        el = document.createElement('p');
        el.className = 'side-cart__error-msg';
        el.setAttribute('role', 'alert');
        const body = this._drawer.querySelector('.side-cart__body');
        if (body) body.prepend(el);
      }
      el.textContent = msg;
      el.classList.add('is-visible');
    }

    _hideError() {
      const el = this._drawer.querySelector('.side-cart__error-msg');
      if (el) el.classList.remove('is-visible');
    }

    _showDiscountError(msg) {
      const el = this._drawer.querySelector('.side-cart__discount-error');
      if (!el) return;
      el.textContent = msg;
      el.classList.add('is-visible');
    }

    _hideDiscountError() {
      const el = this._drawer.querySelector('.side-cart__discount-error');
      if (el) {
        el.textContent = '';
        el.classList.remove('is-visible');
      }
    }

    /* ---------------------------------------------------------------- */
    /*  Helpers                                                           */
    /* ---------------------------------------------------------------- */
    _dispatchCartChanged() {
      document.documentElement.dispatchEvent(
        new CustomEvent(CART_CHANGED, { bubbles: true, cancelable: false })
      );
    }

    _setCookie(name, value, days) {
      const expires = new Date(Date.now() + days * 864e5).toUTCString();
      document.cookie = `${name}=${value}; expires=${expires}; path=/`;
    }

    _getCookie(name) {
      const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1') + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Boot                                                                */
  /* ------------------------------------------------------------------ */
  window.SideCart = new SideCart();

})();
